<template>
  <van-popup position="center" v-model="visible" round :closeable="!busy && !sending" :z-index="5000" get-container="body" class="xsou-password-reset" :close-on-click-overlay="!busy && !sending" @open="open" @closed="clear">
    <h2>{{ $t('passwordReset.title') }}</h2>
    <van-tabs class="password-reset__tabs" :active="mode" color="#2f7cf6" title-active-color="#2f7cf6" @change="switchMode">
      <van-tab name="phone" :title="$t('passwordReset.phone')" :disabled="busy || sending" />
      <van-tab name="email" :title="$t('passwordReset.email')" :disabled="busy || sending" />
    </van-tabs>
    <form @submit.prevent="submit">
      <label>{{ $t(isEmail ? 'passwordReset.email' : 'passwordReset.phone') }}
        <div class="password-reset__account">
          <select v-if="!isEmail" v-model="countryCode" :aria-label="$t('auth.areaCode')" :disabled="busy || sending"><option value="+86">+86</option><option value="+852">+852</option></select>
          <input v-model.trim="account" autocomplete="username" :disabled="busy || sending" :type="isEmail ? 'email' : 'tel'" :placeholder="$t(isEmail ? 'passwordReset.email' : 'passwordReset.phone')" required />
        </div>
      </label>
      <label>{{ $t(isEmail ? 'passwordReset.emailCode' : 'passwordReset.smsCode') }}
        <div class="password-reset__account"><input v-model.trim="code" :disabled="busy" inputmode="numeric" maxlength="4" autocomplete="one-time-code" required />
          <button type="button" :disabled="sending || busy || seconds > 0" @click="send">{{ seconds > 0 ? $t('passwordReset.resend', { seconds }) : $t('passwordReset.send') }}</button></div>
      </label>
      <label>{{ $t('passwordReset.password') }}<input v-model="password" :disabled="busy" type="password" autocomplete="new-password" minlength="8" maxlength="20" required /></label>
      <label>{{ $t('passwordReset.confirmPassword') }}<input v-model="confirmation" :disabled="busy" type="password" autocomplete="new-password" minlength="8" maxlength="20" required /></label>
      <button class="password-reset__submit" type="submit" :disabled="busy || sending">{{ $t(busy ? 'passwordReset.saving' : 'passwordReset.submit') }}</button>
    </form>
  </van-popup>
</template>
<script>
import { sendPasswordResetCode, resetPassword } from '@/api/login';
import { encryptPassword } from '@/utils/passwordEncrypt';
import { Toast, Tabs, Tab } from 'vant';
export default {
  name: 'PasswordResetDialog',
  components: { 'van-tabs': Tabs, 'van-tab': Tab },
  props: { value: Boolean, initialAccount: { type: String, default: '' }, initialCountryCode: { type: String, default: '+86' } },
  data() { return { mode: 'phone', account: '', countryCode: '+86', code: '', password: '', confirmation: '', busy: false, sending: false, seconds: 0, timer: null }; },
  computed: {
    visible: { get() { return this.value; }, set(v) { this.$emit('input', v); } },
    isEmail() { return this.mode === 'email'; },
  },
  watch: {
    account() { this.resetChallenge(); },
    countryCode() { this.resetChallenge(); },
  },
  methods: {
    resetChallenge() { clearInterval(this.timer); this.timer = null; this.seconds = 0; this.code = ''; },
    switchMode(mode) { if (this.busy || this.sending || mode === this.mode) return; this.mode = mode; this.account = ''; this.resetChallenge(); },
    open() { this.mode = this.initialAccount.includes('@') ? 'email' : 'phone'; this.account = this.initialAccount; this.countryCode = this.initialCountryCode; },
    clear() { clearInterval(this.timer); this.timer = null; this.seconds = 0; this.code = ''; this.password = ''; this.confirmation = ''; },
    payload() { return { account: this.account, countryCode: this.countryCode }; },
    validAccount() {
      const ok = this.isEmail ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.account) : (this.countryCode === '+852' ? /^[4-9]\d{7}$/ : /^1[3-9]\d{9}$/).test(this.account);
      if (!ok) Toast(this.$t('passwordReset.invalidAccount'));
      return ok;
    },
    async send() {
      if (this.sending || this.busy || this.seconds || !this.validAccount()) return;
      this.sending = true;
      try {
        const res = await sendPasswordResetCode(this.payload());
        if (res.code !== 200) { Toast(res.msg || this.$t('passwordReset.failed')); return; }
        Toast(this.$t('passwordReset.sent'));
        this.seconds = 60;
        this.timer = setInterval(() => { if (--this.seconds <= 0) { clearInterval(this.timer); this.timer = null; } }, 1000);
      } catch (e) { Toast(e.message || this.$t('passwordReset.failed')); }
      finally { this.sending = false; }
    },
    async submit() {
      if (this.busy || this.sending || !this.validAccount()) return;
      if (!/^\d{4}$/.test(this.code)) { Toast(this.$t('passwordReset.invalidCode')); return; }
      if (this.password.length < 8 || this.password.length > 20) { Toast(this.$t('passwordReset.passwordLength')); return; }
      if (this.password !== this.confirmation) { Toast(this.$t('passwordReset.mismatch')); return; }
      this.busy = true;
      try {
        const password = await encryptPassword(this.password);
        const confirmPassword = await encryptPassword(this.confirmation);
        const res = await resetPassword({ ...this.payload(), code: this.code, password, confirmPassword });
        if (res.code !== 200) { Toast(res.msg || this.$t('passwordReset.failed')); return; }
        Toast(this.$t('passwordReset.success')); this.$emit('success'); this.visible = false;
      } catch (e) { Toast(e.message || this.$t('passwordReset.failed')); }
      finally { this.busy = false; }
    },
  },
  beforeDestroy() { this.clear(); },
};
</script>
<style lang="less">
.xsou-password-reset.van-popup {
  &.van-popup--center {
    position: fixed;
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    margin: 0;
    transform: translate3d(-50%, -50%, 0);
    border-radius: 16px !important;
  }
  width: min(90vw, 440px);
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 24px;
  color: #1f2937;
  h2 { margin: 0 32px 20px 0; font-size: 20px; }
  label { display: block; margin-bottom: 16px; font-size: 14px; }
  input, select, button { box-sizing: border-box; min-height: 44px; border: 1px solid #dde3ee; border-radius: 8px; font: inherit; }
  input { width: 100%; margin-top: 8px; padding: 10px 12px; }
  select { margin-top: 8px; padding: 8px; background: #fff; }
  button { padding: 8px 12px; background: #fff; color: #2f7cf6; cursor: pointer; &:disabled { opacity: .6; cursor: default; } }
  .password-reset__tabs {
    margin-bottom: 20px;
    .van-tabs__wrap { height: 44px; }
    .van-tabs__nav { padding-left: 0; padding-right: 0; }
    .van-tab { font-size: 15px; line-height: normal; }
    .van-tab--active { font-weight: 600; }
  }
  .password-reset__account { display: flex; gap: 8px; input { flex: 1; min-width: 0; } button { flex-shrink: 0; margin-top: 8px; } }
  .password-reset__submit { width: 100%; background: #2f7cf6 !important; color: #fff !important; }
}
</style>
